/**
 * Contact Form Data Formatter
 * Formats contact form data into Firebase-ready schema
 */

import userDeviceInfo from './tracking/userDeviceInfo';

/**
 * Generate unique enquiry ID
 */
function generateEnquiryId() {
  return `enq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format contact form data for Firebase
 * @param {Object} formData - Form data from Contact component
 * @param {string} formData.name - Customer name
 * @param {string} formData.email - Customer email
 * @param {string} formData.phone - Customer phone
 * @param {string} formData.company - Customer company (optional)
 * @param {string} formData.message - Enquiry message
 * @returns {Object} Firebase-ready enquiry object
 */
export function formatContactEnquiry(formData) {
  const deviceInfo = userDeviceInfo.getDeviceInfo();
  const timestamp = new Date();
  const timestampISO = timestamp.toISOString();
  const timestampMs = timestamp.getTime();

  return {
    // Enquiry Identification
    enquiry: {
      id: generateEnquiryId(),
      submittedAt: timestampISO,
      submittedTimestamp: timestampMs,
      status: "new"
    },

    // Customer Information
    customer: {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
      company: formData.company?.trim() || null
    },

    // Enquiry Details
    details: {
      message: formData.message.trim(),
      messageLength: formData.message.trim().length,
      subject: null
    },

    // Source Information
    source: {
      page: window.location.href,
      referrer: document.referrer || "direct",
      device: deviceInfo?.device?.type || "Unknown",
      browser: deviceInfo?.device?.browser || "Unknown",
      location: deviceInfo?.location?.timezone || null
    },

    // Communication Tracking
    communication: {
      whatsappSent: false,
      whatsappTimestamp: null,
      firstResponse: null,
      lastContact: null,
      notes: []
    },

    // Firebase Metadata
    metadata: {
      createdAt: timestampISO,
      updatedAt: timestampISO,
      createdBy: "website-form",
      version: 1
    }
  };
}

/**
 * Update enquiry with WhatsApp status
 * @param {Object} enquiryData - Existing enquiry data
 * @returns {Object} Updated enquiry with WhatsApp status
 */
export function markWhatsAppSent(enquiryData) {
  const timestamp = new Date().toISOString();
  
  return {
    ...enquiryData,
    communication: {
      ...enquiryData.communication,
      whatsappSent: true,
      whatsappTimestamp: timestamp
    },
    metadata: {
      ...enquiryData.metadata,
      updatedAt: timestamp
    }
  };
}

export function logEnquiry(_enquiryData) { // eslint-disable-line no-unused-vars
  if (process.env.NODE_ENV !== 'development') return;
}

/**
 * Validate contact form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result { isValid: boolean, errors: object }
 */
export function validateContactForm(formData) {
  const errors = {};

  // Name validation
  if (!formData.name?.trim()) {
    errors.name = 'Name is required';
  } else if (formData.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  // Email validation
  if (!formData.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Phone validation
  if (!formData.phone?.trim()) {
    errors.phone = 'Phone number is required';
  } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
    errors.phone = 'Please enter a valid phone number';
  } else if (formData.phone.replace(/\D/g, '').length < 10) {
    errors.phone = 'Phone number must be at least 10 digits';
  }

  // Message validation
  if (!formData.message?.trim()) {
    errors.message = 'Message is required';
  } else if (formData.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

export default {
  formatContactEnquiry,
  markWhatsAppSent,
  logEnquiry,
  validateContactForm,
  generateEnquiryId
};
